import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GoogleMap, OverlayView } from '@react-google-maps/api';

const FALLBACK_HOTEL_IMAGE = 'assets/images/hotel.png';

const defaultContainerStyle = {
  width: '100%',
  height: 'calc(100vh - 80px)',
};

const defaultOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: false,
  fullscreenControl: false,
  streetViewControl: false,
  draggable: true,
};

const DEFAULT_CENTER = { lat: 46.39915633505674, lng: 6.19819195527464 };
const DEFAULT_ZOOM = 10;

const SMALL_SCREEN_DEFAULT_CENTER = { lat: 45.90, lng: 6.47 };
const SMALL_SCREEN_DEFAULT_ZOOM = 9;

/**
 * Reference viewport used for all screens <= 705px.
 * This matches the good position you have on the 705px / 741px screen.
 */
const VIEWPORT_705_CENTER = { lat: 45.74, lng: 6.47 };
const VIEWPORT_705_ZOOM = 8.52;

const SMALL_SCREEN_MAX_WIDTH = 991;
const FORCE_705_VIEW_MAX_WIDTH = 705;

/**
 * Lock mobile map height to the same reference used by the correct screen.
 * 741 viewport height - 80 header height = 661px map height.
 */
const MOBILE_REFERENCE_VIEWPORT_HEIGHT = 741;
const MOBILE_REFERENCE_HEADER_HEIGHT = 80;
const MOBILE_REFERENCE_MAP_HEIGHT = `${MOBILE_REFERENCE_VIEWPORT_HEIGHT - MOBILE_REFERENCE_HEADER_HEIGHT}px`;

const CustomMarker = ({ position, hotel, onHotelClick }) => {
  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div
        className="map-marker"
        onClick={(e) => {
          e.stopPropagation();
          onHotelClick(hotel);
        }}
      >
        <div className="marker-body hotel-item">
          <img
            className="hotel-image"
            src={
              hotel?.media?.length
                ? hotel.media[0].original_url
                : FALLBACK_HOTEL_IMAGE
            }
            alt="hotel_image"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = FALLBACK_HOTEL_IMAGE;
            }}
          />

          <div className="hotel-details">
            <span className="hotel-name">{hotel?.name}</span>

            <span className="hotel-rating">
              <img
                src="/assets/images/icon-stars.svg"
                alt="star icon"
                height={13.5}
                width={13.5}
              />

              {hotel?.extra?.google_rating?.rating ? (
                `${Number(hotel.extra.google_rating.rating).toFixed(1)}/5 ${
                  hotel.extra.google_rating.user_ratings_total
                    ? `(${hotel.extra.google_rating.user_ratings_total})`
                    : ''
                }`
              ) : hotel?.extra?.rating ? (
                `${hotel.extra.rating}/5`
              ) : (
                '—'
              )}
            </span>

            <span className="badge-duo">
              {hotel?.duo_capability ? 'DUO' : 'NO DUO'}
            </span>
          </div>
        </div>

        <img
          src="/assets/images/tail.svg"
          alt="images"
          className="marker-tail"
        />
      </div>
    </OverlayView>
  );
};

export const Map = ({
  hotels = [],
  selectedHotel = null,
  mapOptions = defaultOptions,
  containerStyle = defaultContainerStyle,
  onClick,
  onHotelClick,
}) => {
  const [markers, setMarkers] = useState([]);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [responsiveContainerStyle, setResponsiveContainerStyle] = useState(containerStyle);

  const mapRef = useRef(null);

  const getResponsiveContainerStyle = useCallback(() => {
    if (typeof window === 'undefined') {
      return containerStyle;
    }

    const shouldLockMobileHeight =
      window.innerWidth <= FORCE_705_VIEW_MAX_WIDTH &&
      window.innerHeight <= MOBILE_REFERENCE_VIEWPORT_HEIGHT;

    return {
      ...containerStyle,
      height: shouldLockMobileHeight
        ? MOBILE_REFERENCE_MAP_HEIGHT
        : containerStyle.height,
      minHeight: shouldLockMobileHeight
        ? MOBILE_REFERENCE_MAP_HEIGHT
        : containerStyle.minHeight,
    };
  }, [containerStyle]);

  const applyDefaultViewport = useCallback(() => {
    if (!mapRef.current || selectedHotel || typeof window === 'undefined') {
      return;
    }

    const viewportWidth = window.innerWidth;

    let nextCenter = DEFAULT_CENTER;
    let nextZoom = DEFAULT_ZOOM;

    /**
     * From 705px and below:
     * always force the exact same map center and zoom.
     */
    if (viewportWidth <= FORCE_705_VIEW_MAX_WIDTH) {
      nextCenter = VIEWPORT_705_CENTER;
      nextZoom = VIEWPORT_705_ZOOM;
    } else if (viewportWidth <= SMALL_SCREEN_MAX_WIDTH) {
      nextCenter = SMALL_SCREEN_DEFAULT_CENTER;
      nextZoom = SMALL_SCREEN_DEFAULT_ZOOM;
    }

    mapRef.current.setCenter(nextCenter);
    mapRef.current.setZoom(nextZoom);

    setCenter(nextCenter);
    setZoom(nextZoom);
  }, [selectedHotel]);

  useEffect(() => {
    const updateContainerStyle = () => {
      setResponsiveContainerStyle(getResponsiveContainerStyle());
    };

    updateContainerStyle();

    window.addEventListener('resize', updateContainerStyle);
    return () => window.removeEventListener('resize', updateContainerStyle);
  }, [getResponsiveContainerStyle]);

  useEffect(() => {
    setMarkers(
      hotels.map((hotel) => ({
        lat: hotel.address.latitude,
        lng: hotel.address.longitude,
        hotel,
      }))
    );

    if (!selectedHotel) {
      setCenter(DEFAULT_CENTER);
      setZoom(DEFAULT_ZOOM);
    }
  }, [hotels, selectedHotel]);

  useEffect(() => {
    if (selectedHotel) {
      const hotelCenter = {
        lat: selectedHotel.address.latitude,
        lng: selectedHotel.address.longitude,
      };

      setCenter(hotelCenter);
      setZoom(18);

      if (mapRef.current) {
        mapRef.current.setCenter(hotelCenter);
        mapRef.current.setZoom(18);
      }
    }
  }, [selectedHotel]);

  useEffect(() => {
    if (!selectedHotel) {
      applyDefaultViewport();
    }
  }, [applyDefaultViewport, markers.length, selectedHotel]);

  useEffect(() => {
    if (selectedHotel || typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      applyDefaultViewport();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [applyDefaultViewport, selectedHotel]);

  useEffect(() => {
    if (!mapRef.current || selectedHotel || typeof window === 'undefined') {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (window.google?.maps?.event) {
        window.google.maps.event.trigger(mapRef.current, 'resize');
      }

      applyDefaultViewport();
    }, 80);

    return () => window.clearTimeout(timer);
  }, [responsiveContainerStyle, applyDefaultViewport, selectedHotel]);

  const handleMapLoad = (map) => {
    mapRef.current = map;

    if (!selectedHotel) {
      applyDefaultViewport();
    }

    if (onClick) {
      map.addListener('click', (event) => {
        const clickedPosition = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };

        onClick(clickedPosition);
      });
    }
  };

  return (
    <div className="map-container">
      <GoogleMap
        mapContainerStyle={responsiveContainerStyle}
        center={center}
        zoom={zoom}
        options={mapOptions}
        onLoad={handleMapLoad}
      >
        {markers.map((coordinate, index) => (
          <CustomMarker
            key={`${coordinate.hotel?.id || index}-${coordinate.lat}-${coordinate.lng}`}
            position={{ lat: coordinate.lat, lng: coordinate.lng }}
            hotel={coordinate.hotel}
            onHotelClick={() => onHotelClick(coordinate.hotel)}
          />
        ))}
      </GoogleMap>
    </div>
  );
};
